import {
  forwardRef,
  createContext,
  useMemo,
  useContext,
  useCallback,
  useState,
  useEffect,
  useRef
} from 'react';
import type { KeyboardEvent, PropsWithoutRef, CSSProperties, MouseEvent } from 'react';

import Progress from '../Progress';
import {
  useConsolidatedRef,
  useDirection,
  useFocusWithin,
  useI18n,
  useOS,
  useUID
} from '../../hooks';
import Flex from '../Flex';
import Link from '../Link';
import Button from '../Button';
import Actions from '../Actions';
import type { ForwardRefForwardPropsComponent } from '../../types';
import { cap } from '../../utils';
import VisuallyHiddenText from '../VisuallyHiddenText';

import {
  StyledStandardTreeParent,
  StyledToggleIcon,
  StyledToggleIconWrapper,
  StyledStandardTreeItemSubTree,
  StyledStandardTreeLeaf,
  StyledStandardTree,
  StyledNodeInteraction,
  StyledStandardTreeNode,
  StyledNodeActions,
  StyledLabelContent,
  StyledNodeIcon,
  StyledNodeText,
  StyledLoadMoreButton,
  StyledSecondaryText,
  StyledLabelContainer
} from './StandardTree.styles';
import type { TreeProps } from './Tree';
import type {
  StandardTreeContextProps,
  StandardTreeNode,
  StandardTreeProps,
  StandardTreePropsWithDefaults
} from './StandardTree.types';
import helpers from './helpers';

const StandardTreeContext = createContext<
  Pick<
    StandardTreePropsWithDefaults,
    | 'currentNodeId'
    | 'contentRenderer'
    | 'lined'
    | 'onNodeClick'
    | 'onNodeHover'
    | 'onNodeToggle'
    | 'firstNodeId'
    | 'lastNodeId'
    | 'focusedNodeId'
    | 'changeFocusedNodeId'
    | 'selectableParents'
  >
>({
  currentNodeId: undefined,
  lined: false,
  contentRenderer: () => undefined,
  onNodeClick: () => {},
  onNodeToggle: () => {},
  firstNodeId: undefined,
  lastNodeId: undefined,
  focusedNodeId: undefined,
  changeFocusedNodeId: () => {},
  selectableParents: true
});

const NodeRenderer: TreeProps<StandardTreeNode>['nodeRenderer'] = ({
  id,
  label,
  secondary,
  icon,
  depth,
  hasParentSibling,
  nodes,
  expanded = false,
  loading = false,
  subTree,
  actions,
  onClick,
  onPreview,
  onLoadMore,
  href
}) => {
  const {
    currentNodeId,
    contentRenderer,
    onNodeClick,
    onNodeToggle,
    onNodeHover,
    lined,
    focusedNodeId,
    changeFocusedNodeId,
    firstNodeId,
    lastNodeId,
    selectableParents
  } = useContext(StandardTreeContext);
  const current = currentNodeId === id;
  const focusedEl = focusedNodeId === id;
  const firstNode = firstNodeId === id;
  const lastNode = lastNodeId === id;
  const { start, end } = useDirection();
  const { macintosh } = useOS();
  const loadMoreRef = useRef<HTMLButtonElement>(null);
  const t = useI18n();

  const ariaCurrent = useMemo(() => {
    return href ? 'page' : true;
  }, [href]);

  const tabIndex = useMemo(() => {
    let index = -1;
    if (focusedNodeId) {
      index = focusedEl ? 0 : -1;
    } else if (currentNodeId && current) {
      index = 0;
    } else if (currentNodeId && nodes && helpers.getNode(nodes, currentNodeId)) {
      index = current ? 0 : -1;
    } else {
      index = firstNode ? 0 : -1;
    }
    return index;
  }, [focusedNodeId, currentNodeId, current, focusedEl]);

  const handleParentClick = useCallback(
    (e: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => {
      onNodeClick?.(id, e);
      changeFocusedNodeId(id);
    },
    [id, onNodeClick, changeFocusedNodeId]
  );

  const handleParentToggle = useCallback(
    (e: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      onNodeToggle?.(id, e);
      changeFocusedNodeId(id);
    },
    [id, onNodeToggle, changeFocusedNodeId]
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>) => {
      if (['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key)) e.preventDefault();
      if (e.key === 'Enter' && !href) e.preventDefault();
      if (href && onPreview && e.getModifierState('Alt') && e.code === 'KeyP') {
        onPreview({ href });
      }
      switch (e.key) {
        case 'ArrowUp':
          changeFocusedNodeId(id, 'up');
          break;
        case 'ArrowDown':
          changeFocusedNodeId(id, 'down');
          break;
        case `Arrow${cap(end)}`:
          if (nodes) {
            if (expanded) {
              changeFocusedNodeId(id, 'right');
            } else {
              onNodeToggle?.(id);
            }
          }
          break;
        case `Arrow${cap(start)}`:
          if (expanded) {
            onNodeToggle?.(id);
          } else {
            changeFocusedNodeId(id, 'left');
          }
          break;
        case 'Enter':
        case ' ':
          onLoadMore?.(id);
          onClick?.(id, e);
          if (nodes) {
            if (selectableParents) {
              handleParentClick(e);
            } else {
              handleParentToggle(e);
            }
          } else {
            onNodeClick?.(id, e);
            changeFocusedNodeId(id);
          }
          break;
        case 'Home':
          if (firstNodeId) changeFocusedNodeId(firstNodeId);
          break;
        case 'End':
          if (lastNodeId) changeFocusedNodeId(lastNodeId);
          break;
        default:
      }
    },
    [id, end, nodes, expanded, actions, firstNodeId, lastNodeId, changeFocusedNodeId]
  );

  const actionsJSX = useMemo(() => {
    // since Actions may render a frag of buttons and we need a wrapper for flex space-between.
    return actions ? (
      <Flex as={StyledNodeActions} container>
        <Actions
          items={actions.map(action => {
            return {
              ...action,
              compact: true,
              onKeyDown: (e: KeyboardEvent) => {
                if (e.key === 'Enter') e.stopPropagation();
              }
            };
          })}
          menuAt={4}
          menuButtonProps={{
            onClick: (e: MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
            },
            onKeyDown: (e: KeyboardEvent) => {
              e.stopPropagation();
              if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
                e.preventDefault();
              }
            }
          }}
        />
      </Flex>
    ) : null;
  }, [actions]);

  const elRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focusedEl) {
      elRef?.current?.focus();
    }
  }, [focusedEl]);

  const loadMoreButton = onLoadMore ? (
    <Flex container={{ alignItems: 'center' }}>
      <Button
        as={StyledLoadMoreButton}
        variant='link'
        aria-label={label}
        onClick={(e: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => {
          onLoadMore?.(id);
          onClick?.(id, e);
        }}
        ref={loadMoreRef}
      >
        {t('load_more')}
      </Button>
    </Flex>
  ) : undefined;

  const labelContent = useMemo(() => {
    if (contentRenderer) {
      const content = contentRenderer({
        id,
        label,
        secondary,
        icon,
        nodes,
        expanded,
        loading,
        actions,
        onClick,
        onLoadMore,
        href
      });
      return !nodes && (onClick ?? onNodeClick) ? (
        <Flex
          container={{ alignItems: 'center', justify: 'between', gap: 2 }}
          as={StyledNodeInteraction}
          forwardedAs={href ? 'a' : 'div'}
          role='treeitem'
          aria-selected={false}
          aria-current={current ? ariaCurrent : undefined}
          tabIndex={tabIndex}
          onClick={(e: MouseEvent<HTMLElement>) => {
            onClick?.(id, e);
            onNodeClick?.(id, e);
            changeFocusedNodeId(id);
          }}
          href={href}
          onKeyDown={onKeyDown}
          ref={elRef}
        >
          {content}
        </Flex>
      ) : (
        content
      );
    }

    const internal = href ? (
      <Flex container={{ alignItems: 'center', gap: 0.5 }} as={StyledLabelContent}>
        {icon && <StyledNodeIcon name={icon} />}
        <Flex container={{ direction: 'column', pad: [0, 0.5, 0, 0.5] }}>
          <Link forwardedAs='span' href={href} previewable={!!onPreview} onPreview={onPreview}>
            {label}
          </Link>
          {secondary && <StyledSecondaryText variant='secondary'>{secondary}</StyledSecondaryText>}
          <VisuallyHiddenText>
            {t('preview_link_instruction', [macintosh ? 'option' : 'alt'])}
          </VisuallyHiddenText>
        </Flex>
      </Flex>
    ) : (
      // Wrapping in a div for handling of flex space-between when there are node actions.
      <Flex container={{ alignItems: 'center', gap: 0.5 }} as={StyledLabelContent}>
        {icon && <StyledNodeIcon name={icon} />}
        <Flex container={{ direction: 'column', pad: [0, 0, 0, 0.5] }}>
          <StyledNodeText>{label}</StyledNodeText>
          {secondary && <StyledSecondaryText variant='secondary'>{secondary}</StyledSecondaryText>}
        </Flex>
      </Flex>
    );

    return !nodes && (onClick ?? onNodeClick) ? (
      <Flex
        container={{ alignItems: 'center', justify: 'between', gap: 2 }}
        as={StyledNodeInteraction}
        forwardedAs={href ? 'a' : 'div'}
        role='treeitem'
        aria-selected={false}
        aria-current={current ? ariaCurrent : undefined}
        tabIndex={tabIndex}
        onClick={(e: MouseEvent<HTMLElement>) => {
          onClick?.(id, e);
          onNodeClick?.(id, e);
          changeFocusedNodeId(id);
        }}
        href={href}
        onKeyDown={onKeyDown}
        ref={elRef}
      >
        {loadMoreButton ?? internal}
        {actionsJSX}
      </Flex>
    ) : (
      internal
    );
  }, [
    id,
    label,
    icon,
    nodes,
    actions,
    onClick,
    onNodeClick,
    current,
    focusedEl,
    tabIndex,
    changeFocusedNodeId,
    onKeyDown
  ]);

  return (
    <StyledStandardTreeNode
      id={id}
      style={
        {
          '--depth': depth,
          '--has-parent': depth ? 1 : 0,
          '--has-parent-sibling': hasParentSibling ? 1 : 0,
          '--first-visible': firstNode ? 1 : 0,
          '--last-visible': lastNode ? 1 : 0
        } as CSSProperties
      }
      aria-busy={loading}
    >
      {nodes ? (
        <>
          <Flex
            container={{ alignItems: 'center', justify: 'between', gap: 2 }}
            as={StyledStandardTreeParent}
            forwardedAs={href ? 'a' : 'div'}
            role='treeitem'
            aria-selected={false}
            aria-current={current ? ariaCurrent : undefined}
            tabIndex={tabIndex}
            variant='text'
            onClick={selectableParents ? handleParentClick : handleParentToggle}
            onMouseOver={
              onNodeHover ? (e: MouseEvent<HTMLElement>) => onNodeHover(id, e) : undefined
            }
            href={selectableParents ? href : undefined}
            aria-expanded={expanded}
            onKeyDown={onKeyDown}
            ref={elRef}
          >
            <Flex container={{ alignItems: 'center' }} as={StyledLabelContainer}>
              {selectableParents ? (
                <Button
                  as='span'
                  variant='simple'
                  tabIndex={-1}
                  icon
                  compact
                  onClick={handleParentToggle}
                >
                  <StyledToggleIcon name='caret-right' />
                </Button>
              ) : (
                <Flex
                  as={StyledToggleIconWrapper}
                  container={{ justify: 'center', alignItems: 'center' }}
                >
                  <StyledToggleIcon name='caret-right' />
                </Flex>
              )}
              {labelContent}
            </Flex>
            {actionsJSX}
          </Flex>
          <StyledStandardTreeItemSubTree lined={lined} id={`${id}-subtree`}>
            {subTree}
            {loading && <Progress variant='ring' placement='inline' />}
          </StyledStandardTreeItemSubTree>
        </>
      ) : (
        <StyledStandardTreeLeaf
          onMouseOver={onNodeHover ? (e: MouseEvent<HTMLElement>) => onNodeHover(id, e) : undefined}
        >
          {labelContent}
        </StyledStandardTreeLeaf>
      )}
    </StyledStandardTreeNode>
  );
};

const StandardTreeWithNodes: ForwardRefForwardPropsComponent<StandardTreeProps> = forwardRef(
  function StandardTreeWithNodes(
    {
      nodes: initialNodes,
      currentNodeId,
      onNodeClick,
      onNodeFocus,
      onNodeHover,
      onNodeToggle,
      onLoadMore,
      contentRenderer,
      lined = false,
      selectableParents = true,
      ...restProps
    }: PropsWithoutRef<StandardTreeProps>,
    ref: StandardTreeProps['ref']
  ) {
    const loadMoreId = useUID();
    const t = useI18n();

    const nodes = [
      ...helpers.mapTree(initialNodes, node => {
        if (node.onLoadMore && !node.loading) {
          return {
            ...node,
            onLoadMore: undefined,
            nodes: [
              ...(node.nodes ?? []),
              {
                id: `${loadMoreId}-${node.id}`,
                label: `${t('load_more')} - ${node.label}`,
                onLoadMore: () => {
                  node.onLoadMore?.(node.id);
                }
              }
            ]
          };
        }
        return node;
      }),
      ...(onLoadMore
        ? [
            {
              id: loadMoreId,
              label: `${t('load_more')}`,
              onLoadMore
            }
          ]
        : [])
    ];

    const [focusedNodeId, setFocusedNodedId] = useState<string | undefined>();
    const treeRef = useConsolidatedRef(ref);

    const lastNodeId = useMemo(() => {
      return helpers.getDeepestNode(nodes, nodes[nodes.length - 1].id)?.id;
    }, [nodes]);

    const changeFocusedNodeId: StandardTreeContextProps['changeFocusedNodeId'] = useCallback(
      (id, type) => {
        switch (type) {
          case 'up': {
            const previousNode = helpers.getPreviousNode(nodes, id);
            if (previousNode) setFocusedNodedId(previousNode.id);
            break;
          }
          case 'down': {
            const nextNode = helpers.getNextNode(nodes, id);
            if (nextNode) setFocusedNodedId(nextNode.id);
            break;
          }
          case 'left': {
            const parentNode = helpers.getParentNode(nodes, id);
            if (parentNode) setFocusedNodedId(parentNode.id);
            break;
          }
          case 'right': {
            const childNode = helpers.getFirstChildNode(nodes, id);
            if (childNode) setFocusedNodedId(childNode.id);
            break;
          }
          default: {
            if (id !== focusedNodeId) setFocusedNodedId(id);
            break;
          }
        }
      },
      [nodes]
    );

    useEffect(() => {
      if (focusedNodeId) onNodeFocus?.(focusedNodeId);
    }, [focusedNodeId]);

    const onFocusChange = (focused: boolean) => {
      if (!focused) setFocusedNodedId('');
    };

    useFocusWithin([treeRef], onFocusChange);

    return (
      <StandardTreeContext.Provider
        value={useMemo(
          () => ({
            currentNodeId,
            focusedNodeId,
            lastNodeId,
            firstNodeId: nodes[0].id,
            changeFocusedNodeId,
            onNodeClick,
            onNodeHover,
            onNodeToggle,
            contentRenderer,
            lined,
            selectableParents
          }),
          [
            currentNodeId,
            onNodeClick,
            onNodeHover,
            lined,
            focusedNodeId,
            lastNodeId,
            onNodeToggle,
            contentRenderer,
            selectableParents
          ]
        )}
      >
        {/* FIXME: Types are having issues when styled(Tree) is typeof Tree. */}
        <StyledStandardTree
          {...restProps}
          ref={treeRef}
          nodes={nodes}
          nodeRenderer={NodeRenderer}
        />
      </StandardTreeContext.Provider>
    );
  }
);

const StandardTree: ForwardRefForwardPropsComponent<StandardTreeProps> = forwardRef(
  function StandardTree(props: PropsWithoutRef<StandardTreeProps>, ref: StandardTreeProps['ref']) {
    return props.nodes.length > 0 ? <StandardTreeWithNodes {...props} ref={ref} /> : null;
  }
);

export default StandardTree;
